"use client";

import {ReactNode, useRef, useState} from "react";
import SectionTitle from "@/app/components/atomic/molecules/SectionTitle";
import Image from "next/image";
import FAQ from "@/public/images/homepage/Img/FAQ.png";
import ChevronIcon from "@/app/components/homepage/components/ChevronIcon";
import InlineLink from "@/app/components/homepage/components/InlineLink";

interface FaqItem {
    question: string;
    answer: ReactNode;
}

const faqItems: FaqItem[] = [
    {
        question: "Comment choisir la meilleure assurance ?",
        answer: <>Pour choisir la meilleure assurance, comparez les offres en fonction du taux d'assurance, des garanties proposées et de la flexibilité des conditions. Si le budget est votre priorité, vous pouvez par exemple viser une <InlineLink href="/guide/mutuelle-sante/pas-cher">mutuelle santé</InlineLink> pas chère tout en conservant un bon niveau de garanties. Nos comparateurs en ligne sont gratuits et vous donnent un aperçu rapide des offres disponibles sur le marché.</>
    },
    {
        question: "Comment utiliser notre comparateur d'assurances ?",
        answer: <>Utiliser notre comparateur est simple : entrez vos informations, puis comparez les options disponibles. En quelques clics, vous accédez à une liste d'options adaptées à vous et votre budget.</>
    },
    {
        question: "Comment résilier mon ancienne assurance ?",
        answer: <>Pour résilier votre ancienne assurance, il suffit généralement d'envoyer une lettre de résiliation à votre assureur, en respectant les délais légaux applicables à votre contrat. Si vous souhaitez <InlineLink href="/guide/mutuelle-sante/changer">changer de mutuelle</InlineLink>, on vous aide à choisir votre nouveau contrat ; la résiliation de l'ancien reste à votre charge. Pour l'assurance de prêt immobilier, la <InlineLink href="/guide/assurance-pret-immobilier/loi-lemoine">loi Lemoine</InlineLink> permet de résilier à tout moment : dans ce cas, on s'occupe de la démarche de résiliation et de la bascule vers votre nouveau contrat à votre place, moyennant des frais de courtage annoncés en toute transparence.</>
    },
    {
        question: "Comment changer d'assurance via notre comparateur ?",
        answer: <>Une fois que vous avez utilisé notre comparateur, vous pouvez souscrire en ligne et on s'occupe de trouver le meilleur contrat pour vous. Sur l'assurance de prêt immobilier, un expert dédié vous accompagne dans la souscription et gère la résiliation de votre ancien contrat à votre place.</>
    },
    {
        question: "Quelles sont les assurances disponibles sur notre comparateur ?",
        answer: <>Notre comparateur d'assurances couvre aussi bien les besoins du quotidien que les grandes étapes de la vie : l'assurance de prêt immobilier et la mutuelle santé, mais aussi l'<InlineLink href="/guide/assurance-deces">assurance décès</InlineLink>, l'<InlineLink href="/guide/assurance-obseques">assurance obsèques</InlineLink>, l'<InlineLink href="/guide/assurance-dependance">assurance dépendance</InlineLink>, l'<InlineLink href="/guide/assurance-voyage">assurance voyage</InlineLink>, l'<InlineLink href="/guide/assurance-chien-chat">assurance chien et chat</InlineLink> ou encore l'<InlineLink href="/guide/assurance-loisirs/velo"> assurance vélo</InlineLink>. Quel que soit le domaine, la démarche reste la même : vous comparez gratuitement et on s'occupe de trouver le meilleur contrat pour vous.</>
    }
];

function FaqBlock({item, index}: { item: FaqItem; index: number }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLParagraphElement>(null);
    const panelId = `faq-panel-${index}`;
    const headingId = `faq-heading-${index}`;

    return (
        <div className="flex flex-col p-4 bg-white rounded-2xl">
            <button
                id={headingId}
                onClick={() => setIsExpanded(!isExpanded)}
                aria-expanded={isExpanded}
                aria-controls={panelId}
                className="flex w-full justify-between items-center cursor-pointer"
            >
                <h4 className="title-look text-left">
                    {item.question}
                </h4>

                <ChevronIcon open={isExpanded} className="text-pale shrink-0" />
            </button>

            <div
                id={panelId}
                role="region"
                aria-labelledby={headingId}
                className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
                style={{maxHeight: isExpanded ? `${contentRef.current?.scrollHeight ?? 500}px` : "0px"}}
            >
                <p ref={contentRef} className="pt-4">
                    {item.answer}
                </p>
            </div>
        </div>
    );
}

export default function FaqSection() {
    return (
        <section className="flex justify-between max-w-[1200px] mx-auto w-full h-full">
            <div className="flex flex-col w-full mx-auto gap-10 sm:max-w-[650px] max-w-[735px]">
                <SectionTitle
                    level="h2"
                    theme="white"
                    topText={{text: "FAQ", icon: "fa-circle-question"}}
                    className="w-full"
                >
                    On répond à toutes vos questions
                </SectionTitle>

                <div className="flex flex-col gap-4">
                    {faqItems.map((item, index) => (
                        <FaqBlock key={item.question} item={item} index={index} />
                    ))}
                </div>
            </div>

            <div className="relative h-[550px] w-full hidden lg:block translate-x-28 rounded-l-3xl xl:rounded-r-3xl overflow-hidden">
                <Image
                    src={FAQ}
                    alt="Le comparateur d'assurance le mieux noté"
                    className="object-cover object-[center_15%]"
                    fill
                />
            </div>
        </section>
    );
}
